import { useState } from "react";
import { Link } from "react-router-dom";
import { authAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Moon, ArrowLeft, Mail, CheckCircle } from "lucide-react";

const LOGIN_BG = "https://images.unsplash.com/photo-1638251855214-27c07de710f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbmlnaHQlMjBza3klMjBzdGFycyUyMGFic3RyYWN0fGVufDB8fHx8MTc3MjYwNTI4N3ww&ixlib=rb-4.1.0&q=85";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_16338d16-af9f-4ec6-a8f3-cd6da42efe59/artifacts/7xtaj2mx_WhatsApp%20Image%202026-03-02%20at%203.01.19%20PM%20%281%29.jpeg";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setSubmitted(true);
      toast.success("Check your email for reset instructions");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Link>

          <Card className="border-0 shadow-none">
            <CardHeader className="space-y-1 px-0">
              <Link to="/" className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity">
                <img src={LOGO_URL} alt="SleepMed" className="h-12 w-auto" />
                <span className="font-heading font-bold text-2xl text-slate-900">SleepMediary</span>
              </Link>
              <CardTitle className="text-2xl font-bold text-slate-900">Forgot Password</CardTitle>
              <CardDescription className="text-slate-600">
                Enter your email and we'll send you a reset link
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {submitted ? (
                <div className="text-center py-8 animate-fadeIn">
                  <div className="h-16 w-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Check your email</h3>
                  <p className="text-slate-600 mb-6">
                    We've sent password reset instructions to <strong>{email}</strong>
                  </p>
                  <p className="text-sm text-slate-500">
                    Didn't receive the email? Check your spam folder or{" "}
                    <button 
                      onClick={() => setSubmitted(false)}
                      className="text-primary hover:underline"
                    >
                      try again
                    </button>
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="input-base pl-10"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary h-12"
                    disabled={loading}
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Sending...
                      </div>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </form>
              )}

              <div className="mt-6 text-center">
                <p className="text-slate-600">
                  Remember your password?{" "}
                  <Link to="/login" className="text-primary hover:underline font-medium">
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div 
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative"
        style={{
          backgroundImage: `url(${LOGIN_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60"></div>
        <div className="relative text-center text-white">
          <Moon className="h-16 w-16 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl font-bold mb-4">Secure Password Reset</h2>
          <p className="text-lg opacity-80 max-w-md">
            Don't worry, it happens to the best of us. We'll help you get back to tracking your sleep in no time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
